/**
 * safeRelativePath — Validate that a path is a safe, repo-relative reference.
 *
 * Rejects every input that could escape the asset root or change scheme:
 *
 *   - empty / non-string input;
 *   - explicit URL schemes (`http:`, `https:`, `javascript:`, `data:`,
 *     `file:`, `vbscript:`, …) — anything containing `:` before a `/`;
 *   - protocol-relative paths (`//example.com/x`);
 *   - parent-directory traversal (`..` segment, `../`, `..\\`);
 *   - leading slash / backslash (would resolve to the document root);
 *   - URL-encoded variants of the above (e.g. `%2e%2e`, `%2f`);
 *   - control characters (NUL/CR/LF).
 *
 * Returns the input untouched when safe; returns `null` otherwise. The
 * caller decides what to do with `null` (typically: render a static
 * placeholder or fall back to a CLI command).
 *
 * Originally lived inline in `demos/diagrams-viewer`; lifted here so
 * every demo that builds an `<a href>` or `<img src>` from manifest
 * data passes through the same allowlist.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

const UNSAFE_PATTERNS: readonly RegExp[] = [
  /^[a-z][a-z0-9+.-]*:/i, // any scheme — http:, javascript:, data:, file: …
  /^\/\//, // protocol-relative URL
  /^[/\\]/, // absolute path (leading slash or backslash)
  /(?:^|[/\\])\.\.(?:[/\\]|$)/, // .. segment
  /[\x00-\x1f]/, // control characters
  /%2e%2e/i, // URL-encoded ..
  /%2f|%5c/i, // URL-encoded / or \
];

export function safeRelativePath(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  for (const pattern of UNSAFE_PATTERNS) {
    if (pattern.test(trimmed)) return null;
  }
  return trimmed;
}
